import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import blogPosts from '../data/blogPosts';
import Button from '../components/Button';

const renderContent = (content) => {
  return content.map((block, index) => {
    switch (block.type) {
      case 'heading':
        return (
          <h2
            key={index}
            className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4"
          >
            {block.text}
          </h2>
        );
      case 'subheading':
        return (
          <h3
            key={index}
            className="text-xl font-semibold text-gray-800 mt-6 mb-3"
          >
            {block.text}
          </h3>
        );
      case 'paragraph':
        return (
          <p
            key={index}
            className="text-gray-600 leading-relaxed mb-4 text-[17px]"
          >
            {block.text}
          </p>
        );
      case 'list':
        return (
          <ul key={index} className="space-y-3 mb-6 ml-1">
            {block.items.map((item, i) => (
              <li key={i} className="flex gap-3 text-gray-600 text-[17px] leading-relaxed">
                <span className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2.5"></span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      case 'callout':
        return (
          <div
            key={index}
            className="bg-orange-50 border-l-4 border-primary rounded-r-2xl p-6 my-8"
          >
            <p className="text-gray-700 font-medium leading-relaxed text-[17px]">
              {block.text}
            </p>
          </div>
        );
      default:
        return null;
    }
  });
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">📄</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Post Not Found
          </h1>
          <p className="text-gray-500 mb-6">
            The blog post you're looking for doesn't exist.
          </p>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter((p) => p.id !== post.id)
    .sort((a, b) => {
      if (a.category === post.category && b.category !== post.category) return -1;
      if (b.category === post.category && a.category !== post.category) return 1;
      return 0;
    })
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{post.title} | MyMoveAdvisor Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta
          name="keywords"
          content={`${post.category.toLowerCase()}, moving tips, moving guide, ${post.title.toLowerCase().split(' ').slice(0, 5).join(', ')}`}
        />
        <link
          rel="canonical"
          href={`https://mymoveadvisor.com/blog/${post.slug}`}
        />
        <meta property="og:title" content={`${post.title} | MyMoveAdvisor`} />
        <meta property="og:description" content={post.excerpt} />
        <meta
          property="og:url"
          content={`https://mymoveadvisor.com/blog/${post.slug}`}
        />
        <meta property="og:image" content={post.image} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.image} />
      </Helmet>

      {/* Hero Image */}
      <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-gray-900/20"></div>

        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-4 pb-12 md:pb-16">
            {/* Breadcrumb */}
            <nav className="flex mb-6 text-sm font-medium">
              <Link
                to="/"
                className="text-gray-300 hover:text-white transition-colors"
              >
                Home
              </Link>
              <span className="text-gray-500 mx-3">/</span>
              <Link
                to="/blog"
                className="text-gray-300 hover:text-white transition-colors"
              >
                Blog
              </Link>
              <span className="text-gray-500 mx-3">/</span>
              <span className="text-primary truncate max-w-[200px]">
                {post.title}
              </span>
            </nav>

            {/* Badges */}
            <div className="flex flex-wrap gap-3 mb-5">
              <span className="bg-primary text-white text-[10px] px-3 py-1.5 rounded-full font-bold uppercase tracking-wider">
                {post.category}
              </span>
              <span className="bg-white/10 backdrop-blur-md text-white text-[10px] px-3 py-1.5 rounded-full font-bold uppercase tracking-wider">
                {post.readTime}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight max-w-4xl">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <article className="lg:col-span-2">
            {/* Author / Date Bar */}
            <div className="flex items-center gap-4 mb-10 pb-8 border-b border-gray-200">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">
                  {post.author}
                </p>
                <p className="text-gray-400 text-sm">{post.date}</p>
              </div>
            </div>

            {/* Post Content */}
            <div className="max-w-none">{renderContent(post.content)}</div>

            {/* CTA Banner */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 mt-14 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-1/2 h-full bg-primary/10 blur-[100px] rounded-full"></div>
              <div className="relative z-10">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Ready to Find Your Perfect Mover?
                </h3>
                <p className="text-gray-400 mb-6 max-w-lg mx-auto">
                  Compare free quotes from verified moving companies near you.
                  Fast, free, and stress-free.
                </p>
                <Button onClick={() => navigate('/quote')}>
                  Get Free Quotes Now
                </Button>
              </div>
            </div>

            {/* Back to Blog */}
            <div className="mt-10">
              <Link
                to="/blog"
                className="inline-flex items-center text-primary font-semibold hover:underline"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16l-4-4m0 0l4-4m-4 4h18"
                  />
                </svg>
                Back to All Posts
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-8">
            {/* Related Posts */}
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
              <h4 className="text-lg font-bold text-gray-900 mb-6">
                Related Articles
              </h4>
              <div className="space-y-6">
                {relatedPosts.map((related) => (
                  <Link
                    key={related.id}
                    to={`/blog/${related.slug}`}
                    className="group block"
                  >
                    <div className="flex gap-4">
                      <div className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] font-bold text-primary uppercase tracking-widest mb-1 block">
                          {related.category}
                        </span>
                        <h5 className="font-semibold text-gray-800 text-sm group-hover:text-primary transition-colors leading-snug line-clamp-2">
                          {related.title}
                        </h5>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
              <h4 className="text-lg font-bold text-gray-900 mb-6">
                Categories
              </h4>
              <div className="space-y-3">
                {[...new Set(blogPosts.map((p) => p.category))].map((cat) => (
                  <Link
                    key={cat}
                    to="/blog"
                    className="flex items-center justify-between group"
                  >
                    <span className="text-gray-600 group-hover:text-primary transition-colors text-sm font-medium">
                      {cat}
                    </span>
                    <span className="bg-gray-100 text-gray-500 text-xs font-bold px-2.5 py-1 rounded-full group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                      {blogPosts.filter((p) => p.category === cat).length}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Sidebar CTA */}
            <div className="bg-gradient-to-br from-primary to-primary-dark rounded-3xl p-8 text-center">
              <div className="text-4xl mb-3">🚚</div>
              <h4 className="text-xl font-bold text-white mb-2">
                Moving Soon?
              </h4>
              <p className="text-white/80 text-sm mb-5">
                Get matched with verified movers in your area.
              </p>
              <Link to="/quote">
                <button className="w-full bg-white text-primary font-semibold py-3 rounded-xl hover:bg-gray-50 transition-colors">
                  Get Free Quote
                </button>
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {/* More Posts Section */}
      <div className="bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 py-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">
            More Articles You Might Like
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts
              .filter((p) => p.id !== post.id)
              .slice(0, 3)
              .map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group block"
                >
                  <article className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="bg-white/90 backdrop-blur-sm text-gray-700 text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider">
                          {p.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                        <span>{p.date}</span>
                        <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                        <span>{p.readTime}</span>
                      </div>
                      <h4 className="font-bold text-gray-900 group-hover:text-primary transition-colors leading-snug line-clamp-2">
                        {p.title}
                      </h4>
                    </div>
                  </article>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
